import * as React from "react";
import { useTheme, styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useTitle } from "../../hooks/useTitle";
import { useUILabel } from "../../hooks/useUILabel";
import ProjectGrid from "./ProjectGrid";
import ProjectDrawer from "./ProjectDrawer";
import { ProjectStatus } from "../../types/project";
import qs from "qs";
import ProjectGridClient from "./ProjectGridClient";
import { CardBase } from "../../components/global";

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

const StyledTabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  minHeight: "400px",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2, 2, 2.5),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 0, 2),
    minHeight: "auto",
  },
}));

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <StyledTabPanel>{children}</StyledTabPanel>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.02)",
  borderRadius: "16px 16px 0 0",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  padding: theme.spacing(0.5, 1),
  [theme.breakpoints.down("md")]: {
    borderRadius: "14px 14px 0 0",
    padding: theme.spacing(0.5, 0.75),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5, 0, 0),
    borderRadius: "12px 12px 0 0",
    overflowX: "auto",
    scrollbarWidth: "thin",
  },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    minHeight: 56,
    color: theme.palette.text.secondary,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    [theme.breakpoints.down("md")]: {
      minHeight: 52,
      fontSize: "0.9rem",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 48,
      fontSize: "0.85rem",
      paddingInline: theme.spacing(1.5),
    },
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.03)",
    },
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
    backgroundColor: theme.palette.primary.main,
  },
}));

const ProjectPage = () => {
  const workManagementLabel = useUILabel("project.workManagement");
  const draftLabel = useUILabel("project.draft");
  const openWorkLabel = useUILabel("project.openWork");
  const scheduledLabel = useUILabel("project.scheduled");
  const inProgressLabel = useUILabel("project.inProgress");
  const completedLabel = useUILabel("project.completed");
  const cancelledLabel = useUILabel("project.cancelled");

  useTitle(workManagementLabel);

  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [value, setValue] = React.useState(1);
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState(null);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  React.useEffect(() => {
    const searchParams = window.location.search;
    if (searchParams) {
      const params = qs.parse(searchParams.substring(1));
      if (params.t && typeof params.t === "string") {
        const tabIndex = Number(params.t);
        if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
          setValue(tabIndex);
        }
      }
    }
  }, []);
  return (
    <Box
      sx={{
        px: 0,
        py: { xs: 1.5, md: 2 },
      }}
    >
      <CardBase
        useGlassCard={true}
        sx={{
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRadius: { xs: 3, sm: 3.5, md: 4 },
          "& .MuiCardContent-root": {
            px: { xs: 0, sm: 0, md: 0 },
          },
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 12px 40px rgba(0, 0, 0, 0.5)"
                : "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <StyledTabs
          value={value}
          onChange={handleChange}
          variant={isTabletDown ? "scrollable" : "fullWidth"}
          scrollButtons={isTabletDown ? "auto" : false}
          allowScrollButtonsMobile
        >
          <Tab label={draftLabel} {...a11yProps(0)} />
          <Tab label={openWorkLabel} {...a11yProps(1)} />
          <Tab label={scheduledLabel} {...a11yProps(2)} />
          <Tab label={inProgressLabel} {...a11yProps(3)} />
          <Tab label={completedLabel} {...a11yProps(4)} />
          <Tab label={cancelledLabel} {...a11yProps(5)} />
        </StyledTabs>
        <TabPanel value={value} index={0} dir={theme.direction}>
          <ProjectGrid
            status={ProjectStatus.DRAFT}
            onView={(item) => {
              setCurrentItem(item);
              setOpen(true);
            }}
          />
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          <ProjectGrid
            status={ProjectStatus.PUBLISHED}
            onView={(item) => {
              setCurrentItem(item);
              setOpen(true);
            }}
          />
        </TabPanel>
        <TabPanel value={value} index={2} dir={theme.direction}>
          <ProjectGridClient
            status={ProjectStatus.SCHEDULED}
            onView={(item) => {
              setCurrentItem(item);
              setOpen(true);
            }}
          />
        </TabPanel>
        <TabPanel value={value} index={3} dir={theme.direction}>
          <ProjectGridClient
            status={ProjectStatus.IN_PROGRESS}
            onView={(item) => {
              setCurrentItem(item);
              setOpen(true);
            }}
          />
        </TabPanel>
        <TabPanel value={value} index={4} dir={theme.direction}>
          <ProjectGrid
            status={ProjectStatus.COMPLETED}
            onView={(item) => {
              setCurrentItem(item);
              setOpen(true);
            }}
          />
        </TabPanel>
        <TabPanel value={value} index={5} dir={theme.direction}>
          <ProjectGrid
            status={ProjectStatus.CANCELED}
            onView={(item) => {
              setCurrentItem(item);
              setOpen(true);
            }}
          />
        </TabPanel>
      </CardBase>
      <ProjectDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </Box>
  );
};
export default ProjectPage;
